const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

const characterUnits = (character: string) => {
  if (/\s/.test(character)) return 0.32;
  if (/[MW@#%&]/.test(character)) return 0.82;
  if (/[A-ZÁÄČĎÉÍĹĽŇÓÔŔŠŤÚÝŽ]/.test(character)) return 0.64;
  if (/[ilI1|.,:;'`!]/.test(character)) return 0.28;
  if (/[^\u0000-\u024f]/.test(character)) return 0.92;
  return 0.53;
};

export const estimateTextWidth = (text: string, fontSize: number) =>
  [...text].reduce((width, character) => width + characterUnits(character) * fontSize, 0);

const breakWord = (word: string, maxWidth: number, fontSize: number, measureTextWidth: TextWidthMeasurer = estimateTextWidth) => {
  const chunks: string[] = [];
  let current = "";
  for (const character of word) {
    if (current && measureTextWidth(current + character, fontSize) > maxWidth) {
      chunks.push(current);
      current = character;
    } else {
      current += character;
    }
  }
  if (current) chunks.push(current);
  return chunks;
};

export type TextWidthMeasurer = (text: string, fontSize: number) => number;

export const wrapTextApprox = (text: string, maxWidth: number, fontSize: number, measureTextWidth: TextWidthMeasurer = estimateTextWidth) => {
  const lines: string[] = [];
  for (const paragraph of (text || " ").split(/\r?\n/)) {
    const words = paragraph.trim().split(/\s+/).filter(Boolean);
    if (words.length === 0) {
      lines.push("");
      continue;
    }
    let current = "";
    for (const word of words) {
      const pieces = measureTextWidth(word, fontSize) > maxWidth
        ? breakWord(word, maxWidth, fontSize, measureTextWidth)
        : [word];
      for (const piece of pieces) {
        const candidate = current ? `${current} ${piece}` : piece;
        if (current && measureTextWidth(candidate, fontSize) > maxWidth) {
          lines.push(current);
          current = piece;
        } else {
          current = candidate;
        }
      }
    }
    lines.push(current);
  }
  return lines;
};

export interface TextFitInput {
  text: string;
  width: number;
  height: number;
  preferredSize: number;
  minSize?: number;
  lineHeight?: number;
  measureTextWidth?: TextWidthMeasurer;
}

export const fitFontSizeToBox = ({
  text,
  width,
  height,
  preferredSize,
  minSize = 5,
  lineHeight = 1.15,
  measureTextWidth = estimateTextWidth,
}: TextFitInput) => {
  const safeWidth = Math.max(8, width - 4);
  const safeHeight = Math.max(8, height - 2);
  const maximum = clamp(preferredSize, minSize, safeHeight / lineHeight);
  let low = minSize;
  let high = maximum;

  for (let attempt = 0; attempt < 18; attempt += 1) {
    const size = (low + high) / 2;
    const lines = wrapTextApprox(text, safeWidth, size, measureTextWidth);
    const fitsHeight = lines.length * size * lineHeight <= safeHeight;
    const fitsWidth = lines.every((line) => measureTextWidth(line, size) <= safeWidth + 0.5);
    if (fitsHeight && fitsWidth) low = size;
    else high = size;
  }

  return Math.round(clamp(low, minSize, maximum) * 10) / 10;
};
